import React, { useEffect, useState } from "react";
import {
  Modal,
  Calendar,
  Input,
  Button,
  Typography,
  Row,
  Col,
  DatePicker,
  AutoComplete,
  App,
  Select,
  Alert,
  Tag,
  Upload, 
  message,
  Divider,
  Table,
  Card,
  Statistic,
} from "antd";
import { PaperClipOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import "./shortLeaveModal.css";
import { fetchEmployeeShiftDates, fetchEmployeeShiftDetails } from "../../attendanceOverviewThunk";
import { useDispatch, useSelector } from "react-redux";
import { selectShiftDates, selectShiftDatesLoaded, selectShiftDetails } from "../../attendanceOverviewSelectors";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import UnmarkedAttendanceAlert from "../../../Employees/components/UnmarkedAttendanceAlert";

const { Text } = Typography;

const ShortLeaveModal = ({
  visible,
  onClose,
  onShortLeaveSubmit,
  restrictedHolidays = [],
  weekEndDays = [0, 6],
  totalLeaveTaken = 0,
  leaveRemainingBalance = 0,
  isDisabled,
  existingAttendance,
  existingShortLeave,
  allowAttendanceReplacement,
  leaveDetailsSummary,
}) => {
  const { modal } = App.useApp();

  const [dateTime, setDateTime]         = useState(dayjs());
  const [leaveType, setLeaveType]       = useState(null);
  const [fromDate, setFromDate]         = useState(null);
  const [toDate, setToDate]             = useState(null);
  const [notes, setNotes]               = useState("");
  const [leavePortion, setLeavePortion] = useState(null);
  const [noShiftToday, setNoShiftToday] = useState(false);
  const [fileList, setFileList]         = useState([]);  // ← NEW

  const dispatch         = useDispatch();
  const shiftDates       = useSelector(selectShiftDates);
  const shiftDatesLoaded = useSelector(selectShiftDatesLoaded);
  const shiftDetails = useSelector(selectShiftDetails);
  const authInfo = useSelector(selectAuthInfo);
  // Annual Leave and Casual Leave are the same pool — there's no separate
  // "short annual leave" DB value, only short_casual_leave, so this just
  // switches the display label to match the country convention used
  // everywhere else.
  const isIndia = authInfo?.country === "India";
  const casualOrAnnualLabel = isIndia ? "Casual Leave" : "Annual Leave";

  const isSameMonthLoaded = (date) => {
    if (!shiftDates || shiftDates.length === 0) return true;
    const firstShiftDate = dayjs(shiftDates[0]);
    return (
      date.month() === firstShiftDate.month() &&
      date.year()  === firstShiftDate.year()
    );
  };

  useEffect(() => {
    const timer = setInterval(() => setDateTime(dayjs()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (visible) dispatch(fetchEmployeeShiftDates(dayjs()));
  }, [visible]);

  useEffect(() => {
    if (!visible || !shiftDatesLoaded) return;
    const today = dayjs().format("YYYY-MM-DD");
    setNoShiftToday(!shiftDates.includes(today));
  }, [shiftDates, shiftDatesLoaded, visible]);

useEffect(() => {
  if (!fromDate) return;
  dispatch(fetchEmployeeShiftDates(fromDate));
  if (authInfo?.employee_id ){
  dispatch(fetchEmployeeShiftDetails({
    emp_id: authInfo?.employee_id,
    institution_id: authInfo?.institution,
    date: dayjs(fromDate).format("YYYY-MM-DD"),
  }));
  }
  }, [fromDate, authInfo, dispatch]);

  // Reset on close
  useEffect(() => {
    if (!visible) {
      setLeaveType(null);
      setFromDate(null);
      setToDate(null);
      setNotes("");
      setLeavePortion(null);
      setFileList([]);  // ← NEW
    }
  }, [visible]);

  useEffect(() => {
    if (!existingShortLeave) return;
    setLeavePortion(existingShortLeave.leave_portion);
    setLeaveType({
      label:
        existingShortLeave.attendance === "short_sick_leave"
          ? "Sick Leave"
          : casualOrAnnualLabel,
      value: existingShortLeave.attendance,
    });
    setNotes(existingShortLeave.notes || "");
  }, [existingShortLeave, casualOrAnnualLabel]);

  const leaveTypes = [
    { label: "Sick Leave", value: "short_sick_leave" },
    { label: casualOrAnnualLabel, value: "short_casual_leave" },
  ];

  // Company-configured window (Company Profile → HR), delivered with the
  // leave summary; the server enforces the same limit. Before a type is
  // picked, allow the widest window so the picker isn't needlessly narrow.
  const generalBackdateLimit = leaveDetailsSummary?.backdateLimits?.general ?? 7;
  const sickBackdateLimit = leaveDetailsSummary?.backdateLimits?.sick ?? 7;
  const ALLOWED_BACKDATE_DAYS = !leaveType
    ? Math.max(generalBackdateLimit, sickBackdateLimit)
    : leaveType.value === "short_sick_leave"
      ? sickBackdateLimit
      : generalBackdateLimit;

  const disableDate = (current) => {
    if (!current) return false;
    const date            = current.format("YYYY-MM-DD");
    const restrictedDates = restrictedHolidays?.map((h) => h?.restricted_holiday_date) || [];
    const isHoliday       = restrictedDates.includes(date);
    const isWeekend       = weekEndDays.includes(current.day());
    // const isPast          = current.isBefore(dayjs(), "day");
    const isTooOld        = current.isBefore(dayjs().subtract(ALLOWED_BACKDATE_DAYS, "day").startOf("day"));
    const noShift         = isSameMonthLoaded(current) && !shiftDates.includes(date);
    return isHoliday || isTooOld || isWeekend || noShift;
  };

  const handlePanelChange = (value) => {
    dispatch(fetchEmployeeShiftDates(value));
  };

  // ── File upload validation (same rules as saveJournal) ──
  const beforeUpload = (file) => {
    const allowed = ["image/jpeg", "image/png", "application/pdf"];
    const under5MB = file.size / 1024 / 1024 < 5;

    if (!allowed.includes(file.type)) {
      message.error("Only JPG, PNG, or PDF files are allowed.");
      return Upload.LIST_IGNORE;
    }
    if (!under5MB) {
      message.error("File must be under 5MB.");
      return Upload.LIST_IGNORE;
    }

    setFileList((prev) => [...prev, file]);
    return false; // prevent auto-upload
  };

  const handleSave = () => {
    if (!leavePortion)
      return modal.warning({ title: "Select Leave Portion", centered: true });
    if (!leaveType)
      return modal.warning({ title: "Select Leave Type", centered: true });
    if (!fromDate)
      return modal.warning({ title: "Select From Date", centered: true });
    if (!toDate)
      return modal.warning({ title: "Select To Date", centered: true });
    if (!notes?.trim())
      return modal.warning({ title: "Notes is required", centered: true });

    const formattedFromDate = dayjs(fromDate).format("YYYY-MM-DD");
    const formattedToDate   = dayjs(toDate).format("YYYY-MM-DD");

    if (formattedFromDate > formattedToDate)
      return modal.warning({
        title: "The 'From' date cannot be later than the 'To' date.",
        centered: true,
      });

    if (allowAttendanceReplacement) {
      return modal.confirm({
        title: "Replace Attendance?",
        centered: true,
        content:
          "This will replace your recent attendance check-in with a short leave request.",
        okText: "Continue",
        cancelText: "Cancel",

        onOk: () => {
          onShortLeaveSubmit({
            attendance: leaveType.value,
            from_date: formattedFromDate,
            to_date: formattedToDate,
            notes,
            leave_portion: leavePortion,
          });
        },
      });
    }

    // ✅ Pass data to parent for dispatch
    onShortLeaveSubmit({
      attendance:    leaveType.value,
      from_date:     formattedFromDate,
      to_date:       formattedToDate,
      notes,
      leave_portion: leavePortion,
      attachments:   fileList,  // ← NEW
    });
  };

  const canBecomeFullDay =
  existingShortLeave &&
  existingShortLeave.leave_portion !==
    leavePortion;

  const halfTime = shiftDetails?.shift_half_time
    ? dayjs(`${dayjs().format("YYYY-MM-DD")} ${shiftDetails.shift_half_time}`)
    : null;

  // Mirrors the backend's grace window: up to 60 minutes of overlap into the
  // half being claimed as leave is still allowed (submitted late, but the
  // recorded check_in/check_out gets pinned to the exact half-mark, not the
  // actual late time) — beyond that it's blocked. Keep this in sync with the
  // same constant in shortleaveAttendance in hrpayroll.js.
  const GRACE_MINUTES = 60;

  const disableFirstHalf =
    existingAttendance?.attendance === "present" &&
    existingAttendance?.check_in &&
    !allowAttendanceReplacement &&
    (() => {
      const checkInMoment = dayjs(
        `${dayjs().format("YYYY-MM-DD")} ${existingAttendance.check_in}`
      );
      if (!halfTime || !checkInMoment.isBefore(halfTime)) return false;
      return halfTime.diff(checkInMoment, "minute") > GRACE_MINUTES;
    })();

  const disableSecondHalf =
    existingAttendance?.check_out &&
    (() => {
      const checkOutMoment = dayjs(
        `${dayjs().format("YYYY-MM-DD")} ${existingAttendance.check_out}`
      );
      if (!halfTime || !checkOutMoment.isAfter(halfTime)) return false;
      return checkOutMoment.diff(halfTime, "minute") > GRACE_MINUTES;
    })();


    const leaveColumns = [
    {
      title: "Leave Type",
      dataIndex: "leave_type",
      align: "center",
    },
    {
      title: "Total",
      dataIndex: "total",
      align: "center",
      render: (value) =>
        value !== undefined ? value : <Text type="secondary">N/A</Text>,
    },
    {
      title: "Used",
      dataIndex: "used",
      align: "center",
      render: (value) => <Tag color="blue">{value}</Tag>,
    },
    {
      title: "Pending",
      dataIndex: "pending",
      align: "center",
      render: (value) =>
        value ? <Tag color="orange">{value}</Tag> : <Tag>0</Tag>,
    },
    {
      title: "Balance",
      dataIndex: "balance",
      align: "center",
      render: (value) =>
        value !== undefined ? (
          <Tag color="green">{value}</Tag>
        ) : (
          <Text type="secondary">N/A</Text>
        ),
    },
  ];

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={850}
      centered
      className="short-leave-modal"
    >
      {/* Header */}
      <Row justify="space-between" align="middle" style={{ marginTop: "1rem" }}>
        <Col>
          <Text strong className="attendance-modal-title">Short Leave</Text>
        </Col>
        <Col>
          <Text className="attendance-modal-time">
            {dateTime.format("DD-MMM-YYYY hh:mm:ss A")}
          </Text>
        </Col>
      </Row>

      {noShiftToday && (
        <Alert
          type="warning"
          showIcon
          message="No shift assigned for today"
          description="Short leave can only be applied on working shift days. Please contact HR to assign a shift for today."
          style={{ marginTop: 16 }}
        />
      )}

      {allowAttendanceReplacement && (
        <Alert
          type="warning"
          showIcon
          message="Recent check-in detected"
          description="Submitting this short leave will replace your recent attendance check-in."
          style={{ marginTop: 16 }}
        />
      )}

      {canBecomeFullDay && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
          message="Will Convert to Full Day Leave"
          description="Applying this half will merge into a full-day leave request."
        />
      )}

      {/* Calendar */}
      <div
        className="attendanceOverview-calendar-container short-leave-calendar-container"
        style={{ marginTop: "2rem" }}
      >
        {/* <Calendar
          fullscreen={false}
          disabledDate={disableDate}
          onPanelChange={handlePanelChange}
        /> */}

         <div className="leave-summary-header">

            <div>

                <div className="leave-summary-title">
                    Leave Balance Summary
                </div>

                <div className="leave-summary-subtitle">
                    Current leave usage and remaining balance.
                </div>

            </div>

        </div>

        <Table
            rowKey="key"
            size="middle"
            pagination={false}
            columns={leaveColumns}
            dataSource={leaveDetailsSummary?.leaveSummary || []}
        />
      </div>

      <UnmarkedAttendanceAlert
        unmarkedAttendance={leaveDetailsSummary?.unmarkedAttendance}
        style={{ marginTop: 16 }}
      />

      <Divider
          orientation="center"
          style={{ marginTop: "2rem" }}
      >
          Short Leave Application
      </Divider>

      {/* Leave Type + Balance */}
      <Row gutter={16} style={{ marginTop: "2rem" }}>       

        {/* <Col xs={24} sm={12} style={{ display: "flex", gap: "1rem" }}>
          <div style={{ flex: 1 }}>
            <label className="form-label">No. of Days</label>
            <div className="value-box">{totalLeaveTaken}</div>
          </div>
          <div style={{ flex: 1 }}>
            <label className="form-label">Remaining Balance</label>
            <div className="value-box">{leaveRemainingBalance}</div>
          </div>
        </Col> */}

        <Col xs={24} lg={12}>
          <Row gutter={12}>
              <Col span={12}>
                  <Card size="small">
                      <Statistic
                          title="Used"
                          value={totalLeaveTaken}
                      />
                  </Card>
              </Col>

              <Col span={12}>
                  <Card size="small">
                      <Statistic
                          title="Remaining"
                          value={leaveRemainingBalance}
                          valueStyle={{ color: "#52c41a" }}
                      />
                  </Card>
              </Col>
          </Row>
      </Col>

        <Col xs={24} sm={12}>
          {/* <div className="attendance-current-status"> */}
          <Card
              size="small"
              title="Attendance Summary"
              className="attendance-summary-card"
          >
            {shiftDetails && (
              <div className="shift-info-box">
                <Tag color="blue">
                  Shift:
                  {shiftDetails.shift_start}
                  -
                  {shiftDetails.shift_end}
                </Tag>

              </div>
            )}
            <div>
              <span style={{marginRight:"10px"}}>Current Attendance:</span>
              <Tag color="green">
                {existingAttendance?.attendance || "Not Marked"}
              </Tag>
            </div>

            <div>
              <span style={{marginRight:"10px"}}>Check In:</span>
              <span>{existingAttendance?.check_in || "--"}</span>
            </div>

            <div>
              <span style={{marginRight:"10px"}} >Status:</span>
              <Tag color="blue">
                {existingAttendance?.status || "--"}
              </Tag>
            </div>
          {/* </div> */}
          </Card>
        </Col>        

        <Col xs={24} sm={12}>
          <label className="form-label">
            Leave Portion <span className="form-span">*</span>
          </label>
          <Select
            value={leavePortion}
            data-testid="short_leave_portion"
            onChange={setLeavePortion}
            options={[
              { label: "Leave First Half", value: "first_half",  disabled: disableFirstHalf, },
              { label: "Leave Second Half", value: "second_half", disabled: disableSecondHalf, },
            ]}
            placeholder="Select portion of shift"
            style={{ width: "100%" }}
          />
        </Col>

        <Col xs={24} sm={12}>
          <label className="form-label">
            Leave Type <span className="form-span">*</span>
          </label>
          {/* <AutoComplete
            style={{ width: "100%" }}
            data-testid="short_leave_type"
            placeholder="Select Leave Type"
            options={leaveTypes.map((item) => ({
              label: item.label,
              value: item.value,
            }))}
            value={leaveType?.label}
            onSelect={(value, option) =>
              setLeaveType({ label: option.label, value: option.value })
            }
          /> */}
          <Select
              style={{ width: "100%" }}
              placeholder="Select Leave Type"
              value={leaveType?.value}
              data-testid="short_leave_type"
              options={leaveTypes.map(item => ({
                  label: item.label,
                  value: item.value
              }))}
              onChange={(value, option) => {
                  setLeaveType({
                      value,
                      label: option.label
                  });

                  if (value !== "short_sick_leave") {
                      setFileList([]);
                  }
              }}
          />
        </Col>
      </Row>

      {/* Dates */}
      <Row gutter={16} style={{ marginTop: "1.5rem" }}>
        <Col xs={24} sm={12}>
          <label className="form-label">
            From Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            data-testid="short_leave_from_date"
            value={fromDate}
            onChange={(val) => setFromDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
          />
        </Col>
        <Col xs={24} sm={12}>
          <label className="form-label">
            To Date <span className="form-span">*</span>
          </label>
          <DatePicker
            style={{ width: "100%" }}
            data-testid="short_leave_to_date"
            value={toDate}
            onChange={(val) => setToDate(val)}
            disabledDate={disableDate}
            format="DD-MMM-YYYY"
          />
        </Col>
      </Row>

      {/* Supporting Documents ← NEW */}
      <div style={{ marginTop: "1.5rem" }}>
        <label className="form-label">
          Supporting Documents{" "}
          <span style={{ color: "#888", fontSize: 12 }}>(Optional · max 3)</span>
        </label>
        <Upload
          fileList={fileList}
          maxCount={3}
          multiple
          beforeUpload={beforeUpload}
          onRemove={(file) =>
            setFileList((prev) => prev.filter((f) => f.uid !== file.uid))
          }
        >
          <Button
            icon={<PaperClipOutlined />}
            disabled={fileList.length >= 3}
          >
            Attach File
          </Button>
        </Upload>
        <Text type="secondary" style={{ fontSize: 11 }}>
          Accepted: JPG, PNG, PDF · Max 5MB each
        </Text>
      </div>

      {/* Notes */}
      <div style={{ marginTop: "1.5rem" }}>
        <label className="form-label">
          Notes <span className="form-span">*</span>
        </label>
        <Input
          placeholder="Notes"
          data-testid="short_leave_note"
          value={notes}
          onChange={(e) => {
            if (e.target.value.length <= 50) setNotes(e.target.value);
          }}
          onKeyDown={(e) => {
            if (
              notes.length >= 50 &&
              !["Backspace", "Delete", "ArrowLeft", "ArrowRight"].includes(e.key)
            ) {
              e.preventDefault();
            }
          }}
        />
        <Text type="secondary" style={{ fontSize: 12 }}>
          {notes.length}/50 characters
        </Text>
      </div>

      {/* Buttons */}
      <div className="attendance-buttons"
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 24
        }}>
        <Button onClick={onClose}>Discard</Button>
        <Button
          type="primary"
          data-testid="submit_short_leave"
          disabled={
            isDisabled || noShiftToday ||
            !fromDate || !toDate || !leaveType || !notes
          }
          onClick={handleSave}
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default ShortLeaveModal;